import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Button,
} from "@mui/material";
import FlagCircleIcon from "@mui/icons-material/FlagCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import BugReportIcon from "@mui/icons-material/BugReport";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useThemeMode } from "@/context/ThemeContext";
import { DRAWER_WIDTH, NAV_ITEMS } from "./constants";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout, user } = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.requiresAuth || isAuthenticated,
  );

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    onMobileClose();
  };

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "en" ? "hr" : "en");
  };

  let lastSection = "";
  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
        }}
        onClick={() => handleNav("/")}
      >
        <FlagCircleIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            lineHeight: 1,
          }}
        >
          {t("app.name")}
        </Typography>
      </Box>

      <List sx={{ flex: 1, px: 1.5 }}>
        {visibleItems.map((item) => {
          const showSection = item.section && item.section !== lastSection;
          if (item.section) lastSection = item.section;
          return (
            <Box key={item.path}>
              {showSection && (
                <Typography
                  variant="overline"
                  sx={{
                    color: "text.secondary",
                    px: 1.5,
                    pt: 2,
                    pb: 0.5,
                    display: "block",
                  }}
                >
                  {item.section}
                </Typography>
              )}
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNav(item.path)}
                sx={{ mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <item.icon
                    sx={{
                      color:
                        location.pathname === item.path
                          ? "primary.main"
                          : "text.secondary",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t(item.i18nKey)}
                  slotProps={{
                    primary: { sx: { fontSize: 14, fontWeight: 500 } },
                  }}
                />
              </ListItemButton>
            </Box>
          );
        })}
        <ListItemButton
          component="a"
          href="https://bugs.pinehood.tech/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <BugReportIcon sx={{ color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText
            primary={t("nav.reportBug")}
            slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
          />
        </ListItemButton>
      </List>

      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <IconButton onClick={toggleTheme} size="small">
          {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        <Button
          size="small"
          variant={i18n.language === "en" ? "contained" : "outlined"}
          onClick={() => i18n.changeLanguage("en")}
          sx={{ minWidth: 32, px: 1 }}
        >
          EN
        </Button>
        <Button
          size="small"
          variant={i18n.language === "hr" ? "contained" : "outlined"}
          onClick={() => i18n.changeLanguage("hr")}
          sx={{ minWidth: 32, px: 1 }}
        >
          HR
        </Button>
        <IconButton
          size="small"
          onClick={onMobileClose}
          sx={{ display: { xs: "inline-flex", lg: "none" } }}
          aria-label={t("common.closeMenu", { defaultValue: "Close menu" })}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      {isAuthenticated && (
        <>
          <Divider />
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {user?.email?.split("@")[0] || "User"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {user?.email}
              </Typography>
            </Box>
            <IconButton size="small" color="error" onClick={handleLogout}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
